import React, { useState, useEffect } from 'react';
import { formatDate, formatDateForInput } from '../utils/dateUtils';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Grid,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  DialogContentText
} from '@mui/material';
import {
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
  DoNotDisturb as CancelledIcon,
  Edit as EditIcon,
  NewReleases as NewReleasesIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import OrderWorkflowNotesDialog from '../components/OrderWorkflowNotesDialog';
import OrderDetail from '../components/OrderDetail';
import SchoolInvoiceDialog from '../components/SchoolInvoiceDialog';

/**
 * School Workflow Page
 * Manages school orders, reorders, and non-deployments
 */
const SchoolWorkflow = () => {
  const { getToken, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State management
  const [tabValue, setTabValue] = useState(() => {
    const tab = searchParams.get('tab');
    return tab ? parseInt(tab) : 0;
  });
  
  const [schoolOrders, setSchoolOrders] = useState([]);
  const [reorders, setReorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reorderFormLoading, setReorderFormLoading] = useState({});
  const [reorderFormUrls, setReorderFormUrls] = useState({});
  const [reorderFormSnackbar, setReorderFormSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Order detail dialog state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);

  // Notes dialog state
  const [notesDialog, setNotesDialog] = useState({
    open: false,
    title: '',
    entityType: '',
    entityId: null,
    notes: []
  });

  // Invoice dialog state
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [orderToInvoice, setOrderToInvoice] = useState(null);

  // Cancelled schools state
  const [cancelledSchools, setCancelledSchools] = useState([]);
  const [cancelledLoading, setCancelledLoading] = useState(false);
  const [cancelledError, setCancelledError] = useState(null);

  // ── Cancel choice dialog (School Orders tab) ─────────────────────────────
  // First shown when Cancel is clicked: lets the user choose between a simple
  // order cancel and a full early-school cancellation (with fee + Xero invoice).
  const [cancelChoiceDialog, setCancelChoiceDialog] = useState({ open: false, order: null });

  // ── Early cancel dialog (School Orders tab) ──────────────────────────────
  const SCHOOL_EARLY_CANCEL_FEE = 2450.00;
  const [earlyCancelDialog, setEarlyCancelDialog] = useState({
    open: false,
    order: null,
    loading: false,
    error: null,
    cancellationReason: '',
    agreementEndDate: '',
    paidSchoolPrice: 1200.00,
    result: null,
  });

  // Cancel school dialog state (for the Reorders tab)
  const CANCELLATION_FEE = 1450.00;
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    reorder: null,
    loading: false,
    error: null,
    cancellationReason: '',
    isEarlyTermination: false,
    paidModelCost: '',
    agreementEndDate: new Date().toISOString().split('T')[0],
    result: null
  });

  const openCancelDialog = (reorder) => {
    setCancelDialog({
      open: true,
      reorder,
      loading: false,
      error: null,
      cancellationReason: '',
      isEarlyTermination: false,
      paidModelCost: '',
      agreementEndDate: new Date().toISOString().split('T')[0],
      result: null
    });
  };

  const closeCancelDialog = () => {
    if (cancelDialog.loading) return;
    setCancelDialog(prev => ({ ...prev, open: false }));
  };

  const computeCancelFeePreview = () => {
    if (!cancelDialog.isEarlyTermination || !cancelDialog.paidModelCost) return null;
    const annualCost = parseFloat(cancelDialog.paidModelCost);
    if (isNaN(annualCost) || annualCost <= 0) return null;
    const orderEnd = cancelDialog.reorder?.lastOrderDate ? new Date(cancelDialog.reorder.lastOrderDate) : null;
    const today = new Date();
    let remainingMonths = 0;
    if (orderEnd && orderEnd > today) {
      const msLeft = orderEnd.getTime() - today.getTime();
      remainingMonths = parseFloat((msLeft / (1000 * 60 * 60 * 24 * 30.4375)).toFixed(2));
    }
    const proRata = parseFloat(((annualCost / 12) * remainingMonths).toFixed(2));
    const total = parseFloat((proRata + CANCELLATION_FEE).toFixed(2));
    return { remainingMonths: parseFloat(remainingMonths).toFixed(1), proRata, total };
  };

  const handleConfirmCancelSchool = async () => {
    if (!cancelDialog.cancellationReason.trim()) {
      setCancelDialog(prev => ({ ...prev, error: 'Please provide a cancellation reason.' }));
      return;
    }
    if (cancelDialog.isEarlyTermination && !cancelDialog.paidModelCost) {
      setCancelDialog(prev => ({ ...prev, error: 'Please enter the annual paid model cost to calculate the fee.' }));
      return;
    }
    setCancelDialog(prev => ({ ...prev, loading: true, error: null }));
    try {
      const token = await getToken();

      // Schools with no existing order — use the org-cancel endpoint so they still
      // appear in the Cancelled Schools tab and follow the full cancellation process.
      if (!cancelDialog.reorder.id) {
        const orgResponse = await axios.post(
          `${config.apiUrl}/api/cancelled-schools/cancel-org/${cancelDialog.reorder.organizationId}`,
          {
            cancellationReason: cancelDialog.cancellationReason,
            isEarlyTermination: cancelDialog.isEarlyTermination,
            paidModelCost: cancelDialog.isEarlyTermination ? parseFloat(cancelDialog.paidModelCost) : undefined,
            agreementEndDate: cancelDialog.agreementEndDate,
            schoolName: cancelDialog.reorder.schoolName
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (orgResponse.data?.success) {
          setCancelDialog(prev => ({ ...prev, loading: false, result: orgResponse.data.data }));
          fetchReorders();
          fetchCancelledSchools();
        } else {
          setCancelDialog(prev => ({ ...prev, loading: false, error: orgResponse.data?.message || 'Cancellation failed.' }));
        }
        return;
      }

      const response = await axios.post(
        `${config.apiUrl}/api/cancelled-schools/cancel/${cancelDialog.reorder.id}`,
        {
          cancellationReason: cancelDialog.cancellationReason,
          isEarlyTermination: cancelDialog.isEarlyTermination,
          paidModelCost: cancelDialog.isEarlyTermination ? parseFloat(cancelDialog.paidModelCost) : undefined,
          agreementEndDate: cancelDialog.agreementEndDate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setCancelDialog(prev => ({ ...prev, loading: false, result: response.data.data }));
        // Refresh both lists
        fetchReorders();
        fetchCancelledSchools();
      } else {
        setCancelDialog(prev => ({ ...prev, loading: false, error: response.data?.message || 'Cancellation failed.' }));
      }
    } catch (err) {
      setCancelDialog(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || err.message || 'Cancellation failed.'
      }));
    }
  };

  // New Schools state
  const [newSchoolsData, setNewSchoolsData] = useState([]);
  const [newSchoolsLoading, setNewSchoolsLoading] = useState(false);
  const [newSchoolsError, setNewSchoolsError] = useState(null);
  const [newSchoolEditDialog, setNewSchoolEditDialog] = useState({ open: false, record: null });
  const [newSchoolForm, setNewSchoolForm] = useState({
    school_name: '', status: 'Ordered',
    authorised_date: '', demo_date: '', training_date: '', account_created_date: '',
    deployment_1_date: '', deployment_2_date: '', deployment_3_date: '',
    frequency: '', notes: ''
  });
  const [trainingDateDialog, setTrainingDateDialog] = useState({ open: false, recordId: null, date: '' });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Create organization from school order
  const [createOrgDialog, setCreateOrgDialog] = useState({ open: false, order: null, loading: false });

  const handleCreateOrganization = async () => {
    const order = createOrgDialog.order;
    if (!order) return;
    setCreateOrgDialog(prev => ({ ...prev, loading: true }));
    try {
      const token = await getToken();
      const response = await axios.post(
        `${config.apiUrl}/api/school-orders/${order.id}/create-organization`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setSnackbar({
          open: true,
          message: `Organisation "${response.data.organizationName}" created and linked (ID: ${response.data.organizationId})`,
          severity: 'success'
        });
        setCreateOrgDialog({ open: false, order: null, loading: false });
        fetchSchoolOrders();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create organization.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setCreateOrgDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // New school enrolment form state
  const [newSchoolFormLoading, setNewSchoolFormLoading] = useState(false);
  const [newSchoolFormUrl, setNewSchoolFormUrl] = useState(null);
  const [newSchoolDialogOpen, setNewSchoolDialogOpen] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState('');

  const handleOpenNewSchoolDialog = () => {
    setNewSchoolName('');
    setNewSchoolFormUrl(null);
    setNewSchoolDialogOpen(true);
  };

  const handleGenerateNewSchoolForm = async () => {
    if (!newSchoolName.trim()) return;
    setNewSchoolFormLoading(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${config.apiUrl}/api/school-orders/generate-new-school-form`,
        { schoolName: newSchoolName.trim(), googleMapsApiKey: config.googleMapsApiKey || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        const url = response.data.formUrl;
        const orderId = response.data.orderId;
        setNewSchoolFormUrl(url);
        await navigator.clipboard.writeText(url);
        setSnackbar({
          open: true,
          message: `Signup form for "${newSchoolName.trim()}" created (Order #${orderId}) � link copied!`,
          severity: 'success'
        });
        fetchSchoolOrders();
      }
    } catch (err) {
      console.error('Error generating new school form:', err);
      setSnackbar({ open: true, message: 'Failed to generate new school form.', severity: 'error' });
    } finally {
      setNewSchoolFormLoading(false);
    }
  };

  // Invoice Button Component
  const InvoiceButton = ({ orderId }) => {
    const [invoiceInfo, setInvoiceInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      fetchInvoiceInfo();
    }, [orderId]);

    const fetchInvoiceInfo = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const response = await axios.get(`${config.apiUrl}/proof/invoice-info/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success && response.data.data) {
          setInvoiceInfo(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching invoice info:', error);
      } finally {
        setLoading(false);
      }
    };

    if (loading || !invoiceInfo) {
      return null;
    }

    return (
      <Button
        variant="text"
        color="success"
        size="small"
        onClick={() => window.open(invoiceInfo.invoiceLink, '_blank')}
        sx={{ 
          minWidth: 'auto', 
          px: 1, 
          py: 0.25, 
          fontSize: '0.75rem',
          textTransform: 'none'
        }}
      >
        View Invoice
      </Button>
    );
  };

  // Download Invoice Button Component
  const DownloadInvoiceButton = ({ orderId }) => {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
      try {
        setDownloading(true);
        const token = await getToken();
        
        const response = await axios.get(`${config.apiUrl}/api/school-orders/download/invoice/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob' // Important for file downloads
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from content-disposition header or use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = `School-Invoice-${orderId}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        setSnackbar({
          open: true,
          message: 'Invoice downloaded successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error downloading invoice:', error);
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Error downloading invoice',
          severity: 'error'
        });
      } finally {
        setDownloading(false);
      }
    };

    return (
      <Button
        variant="text"
        color="primary"
        size="small"
        onClick={handleDownload}
        disabled={downloading}
        sx={{ 
          minWidth: 'auto', 
          px: 1, 
          py: 0.25, 
          fontSize: '0.75rem',
          textTransform: 'none'
        }}
      >
        {downloading ? 'Downloading...' : 'Download PDF'}
      </Button>
    );
  };

  // Tab Panel component
  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`school-tabpanel-${index}`}
      aria-labelledby={`school-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Update URL params
    const params = new URLSearchParams(searchParams);
    params.set('tab', newValue.toString());
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Fetch school orders
  const fetchSchoolOrders = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/school-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success) {
        // Deduplicate by id � keep last occurrence in case server returns dupes
        const raw = response.data.data || [];
        const seen = new Map();
        raw.forEach(o => seen.set(o.id, o));
        setSchoolOrders([...seen.values()]);
      }
    } catch (err) {
      console.error('Error fetching school orders:', err);
      setError('Failed to load school orders');
    } finally {
      setLoading(false);
    }
  };

  // Fetch cancelled schools
  const fetchCancelledSchools = async () => {
    setCancelledLoading(true);
    setCancelledError(null);
    try {
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/cancelled-schools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.success) {
        setCancelledSchools(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching cancelled schools:', err);
      setCancelledError('Failed to load cancelled schools');
    } finally {
      setCancelledLoading(false);
    }
  };

  // Fetch new schools data
  const fetchNewSchools = async () => {
    setNewSchoolsLoading(true);
    setNewSchoolsError(null);
    try {
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/new-schools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.success) {
        setNewSchoolsData(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching new schools:', err);
      setNewSchoolsError('Failed to load new schools');
    } finally {
      setNewSchoolsLoading(false);
    }
  };

  const handleOpenNewSchoolEdit = (record) => {
    const f = (v) => (v ? formatDateForInput(v) : '');
    setNewSchoolForm({
      school_name: record.school_name || '',
      status: record.status || 'Active',
      authorised_date: f(record.authorised_date),
      demo_date: f(record.demo_date),
      training_date: f(record.training_date),
      account_created_date: f(record.account_created_date),
      deployment_1_date: f(record.deployment_1_date),
      deployment_2_date: f(record.deployment_2_date),
      deployment_3_date: f(record.deployment_3_date),
      frequency: record.frequency || '',
      notes: record.notes || ''
    });
    setNewSchoolEditDialog({ open: true, record });
  };

  const handleSaveNewSchool = async () => {
    const record = newSchoolEditDialog.record;
    if (!record) return;
    try {
      const token = await getToken();
      const payload = { ...newSchoolForm };
      // Convert empty strings to null for date fields
      ['authorised_date', 'demo_date', 'training_date', 'account_created_date',
       'deployment_1_date', 'deployment_2_date', 'deployment_3_date'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });
      await axios.put(`${config.apiUrl}/api/new-schools/${record.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: 'School record updated', severity: 'success' });
      setNewSchoolEditDialog({ open: false, record: null });
      fetchNewSchools();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update school record', severity: 'error' });
    }
  };

  // Convert a stored frequency string to a human-readable label
  const formatFrequency = (freq) => {
    if (!freq) return null;
    const f = freq.toLowerCase().trim();
    if (['weekly', 'fortnightly', 'monthly', '1 per term'].includes(f)) {
      return freq.charAt(0).toUpperCase() + freq.slice(1);
    }
    const m = f.match(/^(\d+)\s*per\s*term$/);
    if (m) {
      const n = parseInt(m[1]);
      if (n === 10) return 'Weekly';
      if (n === 5)  return 'Fortnightly';
      if (n === 3)  return 'Monthly';
      if (n === 1)  return '1 per term';
      return `${n} per term`;
    }
    return freq;
  };

  // Return interval in days between deployments based on frequency (term = 10 weeks = 70 days)
  const getFrequencyIntervalDays = (freq) => {
    if (!freq) return null;
    const f = freq.toLowerCase().trim();
    if (f === 'weekly')      return 7;
    if (f === 'fortnightly') return 14;
    if (f === 'monthly')     return Math.round(70 / 3); // ~23 days
    if (f === '1 per term')  return null;
    const m = f.match(/^(\d+)\s*per\s*term$/);
    if (m) {
      const n = parseInt(m[1]);
      if (n <= 1) return null;
      return Math.round(70 / n);
    }
    return null;
  };

  const addDays = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  const handleQuickSetDate = async (recordId, field, record = null) => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const token = await getToken();
      await axios.put(`${config.apiUrl}/api/new-schools/${recordId}`, { [field]: today }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: 'Date set to today', severity: 'success' });
      fetchNewSchools();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to set date', severity: 'error' });
    }
  };

  const handleOpenTrainingDate = (recordId, existingDate) => {
    const defaultDate = existingDate
      ? existingDate.split('T')[0]
      : new Date().toISOString().split('T')[0];
    setTrainingDateDialog({ open: true, recordId, date: defaultDate });
  };

  const handleSaveTrainingDate = async () => {
    if (!trainingDateDialog.date || !trainingDateDialog.recordId) return;
    try {
      const token = await getToken();
      await axios.put(
        `${config.apiUrl}/api/new-schools/${trainingDateDialog.recordId}`,
        { training_date: trainingDateDialog.date },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: 'Training date saved', severity: 'success' });
      setTrainingDateDialog({ open: false, recordId: null, date: '' });
      fetchNewSchools();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save training date', severity: 'error' });
    }
  };

  // Update ePublisher lifecycle status for a cancelled school
  const handleUpdateEPublisherStatus = async (cancelledId, newStatus) => {
    try {
      const token = await getToken();
      await axios.patch(
        `${config.apiUrl}/api/cancelled-schools/${cancelledId}/status`,
        { ePublisherStatus: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCancelledSchools(prev =>
        prev.map(cs => cs.id === cancelledId ? { ...cs, ePublisherStatus: newStatus } : cs)
      );
      setSnackbar({ open: true, message: `ePublisher status updated to "${newStatus}"`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update ePublisher status', severity: 'error' });
    }
  };

  // Fetch reorders data
  const fetchReorders = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${config.apiUrl}/api/school-orders/reorders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.success) {
        const data = response.data.data || [];
        setReorders(data);
        // Seed form URLs from persisted DB values so they survive page refresh
        const urls = {};
        data.forEach(r => {
          if (r.organizationId && r.reorderFormUrl) {
            urls[r.organizationId] = r.reorderFormUrl;
          }
        });
        setReorderFormUrls(prev => ({ ...prev, ...urls }));
      }
    } catch (err) {
      console.error('Error fetching reorders:', err);
      if (err.response?.status !== 404) {
        console.warn('Unexpected error fetching reorders:', err.message);
      }
      setReorders([]);
    }
  };

  // Generate a reorder form for a school
  const handleSendReorder = async (reorder) => {
    const orgId = reorder.organizationId;
    if (!orgId) {
      setReorderFormSnackbar({ open: true, message: 'No organization linked to this school order.', severity: 'error' });
      return;
    }
    setReorderFormLoading(prev => ({ ...prev, [orgId]: true }));
    try {
      const token = await getToken();
      const response = await axios.post(
        `${config.apiUrl}/api/school-orders/generate-reorder-form/${orgId}`,
        { googleMapsApiKey: config.googleMapsApiKey || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        const url = response.data.formUrl;
        setReorderFormUrls(prev => ({ ...prev, [orgId]: url }));
        setReorderFormSnackbar({ open: true, message: 'Reorder form generated! Copy the link to send to the school.', severity: 'success' });
      }
    } catch (err) {
      console.error('Error generating reorder form:', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to generate reorder form.';
      setReorderFormSnackbar({ open: true, message: `Failed to generate reorder form: ${errMsg}`, severity: 'error' });
    } finally {
      setReorderFormLoading(prev => ({ ...prev, [orgId]: false }));
    }
  };

  const handleCopyFormUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setReorderFormSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
    });
  };

  useEffect(() => {
    fetchSchoolOrders();
    fetchReorders();
    fetchCancelledSchools();
    fetchNewSchools();
  }, []);

  // Handle order actions
  const handleViewOrder = async (order) => {
    try {
      // Fetch fresh order data like the Orders page does
      const token = await getToken();
      const endpoint = `${config.apiUrl}/api/school-orders/${order.id}`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let orderData;
      if (response.data && response.data.hasOwnProperty('success') && response.data.hasOwnProperty('data')) {
        orderData = response.data.data;
      } else {
        orderData = response.data;
      }
      
      setSelectedOrder(orderData);
      setOpenDetailDialog(true);
    } catch (err) {
      console.error('Error fetching order details:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load order details',
        severity: 'error'
      });
    }
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedOrder(null);
  };

  const handleInvoiceGenerated = async (orderId, invoiceData) => {
    // Refresh the orders list to show updated status
    await fetchSchoolOrders();
    
    // Close the invoice dialog
    setInvoiceDialogOpen(false);
    setOrderToInvoice(null);
    
    // Show success message
    setSnackbar({
      open: true,
      message: `Invoice generated successfully for Order #${orderId}`,
      severity: 'success'
    });
  };

  const handleCloseInvoiceDialog = () => {
    setInvoiceDialogOpen(false);
    setOrderToInvoice(null);
  };

  const handleOrderUpdate = (updatedOrder) => {
    // Refresh the orders list when an order is updated
    fetchSchoolOrders();
  };

  const handleOpenNotes = (order) => {
    // Build existing notes from order data (similar to OrderWorkflow page)
    const existingNotes = [];
    if (order.notes) {
      existingNotes.push({
        id: 'general',
        content: order.notes,
        createdBy: 'Order Team',
        createdAt: order.createdAt || new Date().toISOString(),
        isNew: false,
        noteType: 'general'
      });
    }
    if (order.designNotes) {
      existingNotes.push({
        id: 'design',
        content: order.designNotes,
        createdBy: 'Design Team',
        createdAt: order.createdAt || new Date().toISOString(),
        isNew: false,
        noteType: 'design'
      });
    }

    setNotesDialog({
      open: true,
      title: `Order #${order.id} Notes`,
      entityType: 'order',
      entityId: order.id,
      notes: existingNotes
    });
  };

  const handleCloseNotesDialog = () => {
    setNotesDialog({
      open: false,
      title: '',
      entityType: '',
      entityId: null,
      notes: []
    });
  };

  // Helper function to get total note count including localStorage
  const getTotalNoteCount = (entityType, entity) => {
    let dbNoteCount = 0;
    if (entityType === 'order') {
      if (entity.notes) dbNoteCount++;
      if (entity.designNotes) dbNoteCount++;
    }
    
    // Count localStorage notes
    const entityId = entity.id;
    const storageKey = `notes_${entityType}_${entityId}`;
    const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    return dbNoteCount + storageNotes.length;
  };

  // Handle notes update from the dialog - update local state
  const handleNotesUpdate = async (updatedNotes, newNote, options = {}) => {
    
    // Update the relevant local state to trigger UI refresh
    if (notesDialog.entityType === 'order') {
      setSchoolOrders(prev => prev.map(order => 
        order.id.toString() === notesDialog.entityId 
          ? { 
              ...order, 
              notes: order.notes || 'Has notes',
              hasLocalStorageNotes: true
            }
          : order
      ));
    }
    
    setSnackbar({ 
      open: true, 
      message: 'Note added successfully!', 
      severity: 'success' 
    });
    
    // Only reload data if this was a successful backend save (not localStorage-only)
    if (!options.localOnly) {
      try {
        await fetchSchoolOrders();
      } catch (error) {
      }
    } else {
    }
  };

  const handleStatusAction = async (orderId, newStatus, notes = '') => {
    // Special handling for Invoice action - open dialog instead of direct status update
    if (newStatus === 'Invoiced') {
      const order = schoolOrders.find(o => o.id === orderId);
      setOrderToInvoice(order);
      setInvoiceDialogOpen(true);
      return;
    }

    // Special handling for Cancel — show choice dialog
    if (newStatus === 'Cancelled') {
      const order = schoolOrders.find(o => o.id === orderId);
      setCancelChoiceDialog({ open: true, order });
      return;
    }

    // Handle other status updates normally
    try {
      const token = await getToken();
      
      // Use the correct endpoint for school orders
      const response = await axios.put(
        `${config.apiUrl}/api/school-orders/${orderId}`,
        { status: newStatus, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        // Refresh the orders list
        await fetchSchoolOrders();
        setSnackbar({
          open: true,
          message: `Order status updated to ${newStatus}`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.data?.message || 'Failed to update order status',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      setSnackbar({
        open: true,
        message: `Error updating order status: ${err.response?.data?.message || err.message}`,
        severity: 'error'
      });
    }
  };

  // ── Simple order-only cancel (School Orders tab) ──────────────────────────
  const handleSimpleOrderCancel = async () => {
    const order = cancelChoiceDialog.order;
    setCancelChoiceDialog({ open: false, order: null });
    try {
      const token = await getToken();
      const response = await axios.put(
        `${config.apiUrl}/api/school-orders/${order.id}`,
        { status: 'Cancelled', notes: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        await fetchSchoolOrders();
        setSnackbar({ open: true, message: 'Order cancelled.', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: response.data?.message || 'Failed to cancel order.', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || err.message || 'Failed to cancel order.', severity: 'error' });
    }
  };

  // ── Open the early-cancel dialog (fetches paidSchoolPrice from settings) ──
  const openEarlyCancelDialog = async (order) => {
    setCancelChoiceDialog({ open: false, order: null });
    let paidSchoolPrice = 1200.00;
    try {
      const token = await getToken();
      const settingsResp = await axios.get(`${config.apiUrl}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (settingsResp.data?.success && settingsResp.data.data?.paidSchoolPrice) {
        paidSchoolPrice = parseFloat(settingsResp.data.data.paidSchoolPrice) || 1200.00;
      }
    } catch (_) { /* use default */ }

    setEarlyCancelDialog({
      open: true,
      order,
      loading: false,
      error: null,
      cancellationReason: '',
      agreementEndDate: order.endDate ? formatDateForInput(order.endDate) : new Date().toISOString().split('T')[0],
      paidSchoolPrice,
      result: null,
    });
  };

  // ── Fee preview for the early-cancel dialog ───────────────────────────────
  const computeEarlyCancelFeePreview = () => {
    const { agreementEndDate, paidSchoolPrice } = earlyCancelDialog;
    if (!agreementEndDate) return null;
    const endDate = new Date(agreementEndDate);
    const today   = new Date();
    let remainingMonths = 0;
    if (endDate > today) {
      const msLeft = endDate.getTime() - today.getTime();
      remainingMonths = parseFloat((msLeft / (1000 * 60 * 60 * 24 * 30.4375)).toFixed(2));
    }
    const proRata = parseFloat(((paidSchoolPrice / 12) * remainingMonths).toFixed(2));
    const total   = parseFloat((proRata + SCHOOL_EARLY_CANCEL_FEE).toFixed(2));
    return { remainingMonths: parseFloat(remainingMonths).toFixed(1), proRata, total };
  };

  // ── Submit early cancellation (School Orders tab) ─────────────────────────
  const handleConfirmEarlyCancelSchool = async () => {
    if (!earlyCancelDialog.cancellationReason.trim()) {
      setEarlyCancelDialog(prev => ({ ...prev, error: 'Please provide a cancellation reason.' }));
      return;
    }
    setEarlyCancelDialog(prev => ({ ...prev, loading: true, error: null }));
    try {
      const token = await getToken();
      const response = await axios.post(
        `${config.apiUrl}/api/cancelled-schools/cancel-early/${earlyCancelDialog.order.id}`,
        {
          cancellationReason: earlyCancelDialog.cancellationReason,
          agreementEndDate:   earlyCancelDialog.agreementEndDate,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setEarlyCancelDialog(prev => ({ ...prev, loading: false, result: response.data.data, warning: response.data.warning }));
        fetchSchoolOrders();
        fetchCancelledSchools();
      } else {
        setEarlyCancelDialog(prev => ({ ...prev, loading: false, error: response.data?.message || 'Cancellation failed.' }));
      }
    } catch (err) {
      setEarlyCancelDialog(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || err.message || 'Cancellation failed.',
      }));
    }
  };

  // Status color mapping
    const getStatusColor = (status) => {
    const statusColors = {
      'Signup': 'secondary',
      'New': 'info',
      'Ordered': 'primary',
      'Invoiced': 'warning',
      'Approved': 'success',
      'Cancelled': 'error',
      'Delivered': 'success'
    };
    return statusColors[status] || 'default';
  };

  // Get product color based on product type
  const getProductColor = (product) => {
    const productColors = {
      'SchoolLink': 'success',  // Green
      'ePublisher': 'primary',  // Blue
      'Paper': 'warning'        // Yellow/Orange
    };
    return productColors[product] || 'default';
  };

  // Get payment model color
  const getPaymentModelColor = (paymentModel) => {
    const paymentColors = {
      'Paid': 'secondary',      // Purple
      'Ad Supported': 'success' // Green
    };
    return paymentColors[paymentModel] || 'default';
  };

  // Get action buttons based on status
  const getActionButtons = (order) => {
    const status = order.status;
    
    const getAvailableActions = () => {
      // Show Create Org on any order that has no org linked yet (and isn't cancelled)
      const createOrgAction = !order.organizationId && status !== 'Cancelled' ? [{
        label: 'Create Org',
        action: () => setCreateOrgDialog({ open: true, order, loading: false }),
        color: 'secondary'
      }] : [];

      const baseActions = [
        { label: 'View', action: () => handleViewOrder(order), color: 'primary' }
      ];

      switch (status) {
        case 'Signup':
          return [
            ...baseActions,
            ...(order.signupFormUrl ? [{
              label: 'Copy Link',
              action: () => {
                navigator.clipboard.writeText(order.signupFormUrl);
                setSnackbar({ open: true, message: 'Signup link copied to clipboard!', severity: 'success' });
              },
              color: 'info'
            }] : []),
            ...createOrgAction,
            { label: 'Cancel', action: () => handleStatusAction(order.id, 'Cancelled'), color: 'error' }
          ];
        case 'Ordered':
          return [
            ...baseActions,
            ...createOrgAction,
            { label: 'New', action: () => handleStatusAction(order.id, 'New'), color: 'info' },
            { label: 'Cancel', action: () => handleStatusAction(order.id, 'Cancelled'), color: 'error' }
          ];
        case 'New':
          return [
            ...baseActions,
            ...createOrgAction,
            { label: 'Approved', action: () => handleStatusAction(order.id, 'Approved'), color: 'success' },
            { label: 'Cancel', action: () => handleStatusAction(order.id, 'Cancelled'), color: 'error' }
          ];
        case 'Invoiced':
          return [
            ...baseActions,
            ...createOrgAction,
            { label: 'View Invoice', component: <InvoiceButton orderId={order.id} />, isComponent: true },
            { label: 'Download PDF', component: <DownloadInvoiceButton orderId={order.id} />, isComponent: true },
            { label: 'Approved', action: () => handleStatusAction(order.id, 'Approved'), color: 'success' },
            { label: 'Cancel', action: () => handleStatusAction(order.id, 'Cancelled'), color: 'error' }
          ];
        case 'Approved':
          return [
            ...baseActions,
            ...createOrgAction,
            { label: 'Cancel', action: () => handleStatusAction(order.id, 'Cancelled'), color: 'error' }
          ];
        default:
          return [...baseActions, ...createOrgAction];
      }
    };

    const availableActions = getAvailableActions();

    return (
      <ButtonGroup size="small" variant="text">
        {availableActions.map((actionItem, index) => {
          if (actionItem.isComponent) {
            return <React.Fragment key={index}>{actionItem.component}</React.Fragment>;
          }
          
          return (
            <Button 
              key={index}
              color={actionItem.color}
              onClick={actionItem.action}
              size="small"
              sx={{ 
                minWidth: 'auto', 
                px: 1, 
                py: 0.25, 
                fontSize: '0.75rem',
                textTransform: 'none'
              }}
            >
              {actionItem.label}
            </Button>
          );
        })}
      </ButtonGroup>
    );
  };

  return (
    <>
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            School Workflow
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage school orders, reorders, and deployments
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Orders
                  </Typography>
                  <Typography variant="h4">
                    {schoolOrders.filter(order => ['Ordered', 'New', 'Invoiced'].includes(order.status)).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active orders
                  </Typography>
                </Box>
                <AssignmentIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    New Orders
                  </Typography>
                  <Typography variant="h4">
                    {schoolOrders.filter(order => order.status === 'New').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Awaiting processing
                  </Typography>
                </Box>
                <SchoolIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Invoiced
                  </Typography>
                  <Typography variant="h4">
                    {schoolOrders.filter(order => order.status === 'Invoiced').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Awaiting approval
                  </Typography>
                </Box>
                <ReceiptIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Revenue
                  </Typography>
                  <Typography variant="h4">
                    ${(() => {
                      const activeOrders = schoolOrders.filter(order => order.status && order.status.toLowerCase() !== 'cancelled');
                      const total = activeOrders.reduce((sum, order) => sum + (parseFloat(order.totalPrice) || 0), 0);
                      return total >= 1000 ? `${(total / 1000).toFixed(1)}K` : total.toFixed(0);
                    })()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total value
                  </Typography>
                </Box>
                <ReceiptIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="school workflow tabs">
              <Tab label="School Orders" icon={<SchoolIcon />} />
              <Tab label="Reorders" icon={<RefreshIcon />} />
              <Tab label="Non Deployments" icon={<BlockIcon />} />
              <Tab label="Cancelled Schools" icon={<CancelledIcon />} />
              <Tab label="New Schools" icon={<NewReleasesIcon />} />
            </Tabs>
          </Box>

          {/* School Orders Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">School Orders Management</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenNewSchoolDialog}
                >
                  New School Order
                </Button>
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Organisation</TableCell>
                        <TableCell>Products</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Payment Model</TableCell>
                        <TableCell>Order Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Documents</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {schoolOrders
                        .filter(order => order.status && order.status.toLowerCase() !== 'cancelled')
                        .map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              #{order.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {order.schoolName || order.organizationName || order.businessName || (order.status === 'Signup' ? 'Pending Signup' : 'Unknown Organisation')}
                            </Typography>
                            {order.organizationId ? (
                              <Typography variant="caption" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                                ? Org #{order.organizationId}
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                                ? No organization linked
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {order.productsOrdered ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 220 }}>
                                {order.productsOrdered.split(',').map((product, index) => {
                                  const trimmedProduct = product.trim();
                                  const productColor = getProductColor(trimmedProduct);

                                  return (
                                    <Chip
                                      key={index}
                                      label={trimmedProduct}
                                      size="small"
                                      variant="outlined"
                                      color={productColor}
                                      sx={{ 
                                        fontSize: '0.75rem',
                                        height: 24,
                                        fontWeight: 500,
                                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                                        '&:hover': {
                                          bgcolor: 'rgba(255, 255, 255, 1)',
                                          transform: 'scale(1.02)'
                                        },
                                        transition: 'all 0.15s ease-in-out'
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No products
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {order.totalPrice ? `$${parseFloat(order.totalPrice).toFixed(2)}` : '$0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.paymentModel || 'Not specified'}
                              variant="outlined"
                              size="small"
                              color={getPaymentModelColor(order.paymentModel)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.createdAt ? formatDate(order.createdAt) : 'Unknown date'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={order.status || 'New'} 
                              color={getStatusColor(order.status)} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const apiBase = config.apiUrl;
                              const invLink = order.invoiceUrl ? `${apiBase}/proof/invoice/${order.invoiceUrl}` : null;
                              const certLink = order.certToken ? `${apiBase}/reorders/certificates/cert-${order.certToken}.html` : null;
                              return (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {invLink && (
                                    <Button size="small" variant="outlined" color="primary"
                                      onClick={(e) => { e.stopPropagation(); window.open(invLink, '_blank'); }}
                                      sx={{ minWidth: 0, px: 1, fontSize: '0.7rem', py: 0.25 }}>
                                      Invoice
                                    </Button>
                                  )}
                                  {certLink && (
                                    <Button size="small" variant="outlined" color="secondary"
                                      onClick={(e) => { e.stopPropagation(); window.open(certLink, '_blank'); }}
                                      sx={{ minWidth: 0, px: 1, fontSize: '0.7rem', py: 0.25 }}>
                                      Certificate
                                    </Button>
                                  )}
                                  {!invLink && !certLink && <Typography variant="caption" color="text.disabled">�</Typography>}
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="text"
                              size="small"
                              color="info"
                              onClick={() => handleOpenNotes(order)}
                              sx={{ 
                                minWidth: 'auto', 
                                px: 1, 
                                py: 0.25, 
                                fontSize: '0.75rem',
                                textTransform: 'none'
                              }}
                            >
                              Notes
                              {(() => {
                                const totalCount = getTotalNoteCount('order', order);
                                return totalCount > 0 ? (
                                  <Box
                                    component="span"
                                    sx={{
                                      ml: 0.5,
                                      bgcolor: 'info.main',
                                      color: 'info.contrastText',
                                      px: 0.5,
                                      py: 0.1,
                                      borderRadius: '50%',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      minWidth: 18,
                                      height: 18,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {totalCount}
                                  </Box>
                                ) : null;
                              })()}
                            </Button>
                          </TableCell>
                          <TableCell>
                            {getActionButtons(order)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {schoolOrders.filter(order => order.status && order.status.toLowerCase() !== 'cancelled').length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                              No active school orders found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>

          {/* Reorders Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">School Reorders</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Schools in Reorder status (expired orders) or with orders ending within the next 3 months.
              </Typography>

              {reorders.length === 0 ? (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>No Schools Require Reordering</Typography>
                    <Typography variant="body2" color="text.secondary">
                      There are currently no schools in reorder status or with orders ending within 3 months.
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell>Last Order End</TableCell>
                        <TableCell>Products</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Reorder Form</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reorders.map((reorder) => {
                        const orgId = reorder.organizationId;
                        const formUrl = reorderFormUrls[orgId];
                        const isLoading = reorderFormLoading[orgId];
                        const isExpired = reorder.reorderType === 'expired' || (reorder.lastOrderDate && new Date(reorder.lastOrderDate) < new Date());
                        return (
                          <TableRow key={reorder.id} hover>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{reorder.schoolName}</Typography>
                                {isExpired && (
                                  <Chip label="Expired" color="error" size="small" sx={{ mt: 0.5, height: 18, fontSize: '0.7rem' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{reorder.lastOrderDate ? formatDate(reorder.lastOrderDate) : '�'}</TableCell>
                            <TableCell>{reorder.products || '�'}</TableCell>
                            <TableCell>
                              <Chip
                                label={reorder.priority}
                                color={reorder.priority === 'High' ? 'error' : reorder.priority === 'Medium' ? 'warning' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {formUrl ? (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CopyIcon />}
                                    onClick={() => handleCopyFormUrl(formUrl)}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                  >
                                    Copy Link
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={() => window.open(formUrl, '_blank')}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                  >
                                    Preview
                                  </Button>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">Not generated yet</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="primary"
                                  startIcon={<SendIcon />}
                                  disabled={isLoading}
                                  onClick={() => handleSendReorder(reorder)}
                                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  {isLoading ? 'Generating...' : formUrl ? 'Regenerate' : 'Send Reorder'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  startIcon={<CancelIcon />}
                                  onClick={() => openCancelDialog(reorder)}
                                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  Cancel School
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Snackbar for form URL feedback */}
            <Snackbar
              open={reorderFormSnackbar.open}
              autoHideDuration={4000}
              onClose={() => setReorderFormSnackbar(prev => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert severity={reorderFormSnackbar.severity} onClose={() => setReorderFormSnackbar(prev => ({ ...prev, open: false }))}>
                {reorderFormSnackbar.message}
              </Alert>
            </Snackbar>
          </TabPanel>

          {/* Non Deployments Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Non Deployments
              </Typography>
              <Card>
                <CardContent>
                  <Typography variant="body1" gutterBottom>
                    Coming Soon
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This section will contain information about non-deployments when implemented.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </TabPanel>

          {/* Cancelled Schools Tab */}
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6">Cancelled Schools</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Schools that have cancelled or not renewed their agreement. Each school has 1 month of account access
                    after the Agreement End Date, then the account is closed in ePublisher. Data is preserved for 3 more
                    months before deletion.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchCancelledSchools}
                  disabled={cancelledLoading}
                  size="small"
                >
                  Refresh
                </Button>
              </Box>

              {cancelledError && (
                <Alert severity="error" sx={{ mb: 2 }}>{cancelledError}</Alert>
              )}

              {cancelledLoading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                  <CircularProgress />
                </Box>
              ) : cancelledSchools.length === 0 ? (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>No Cancelled Schools</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Schools that cancel or do not renew will appear here.
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Agreement End Date</TableCell>
                        <TableCell>Account Closure Date</TableCell>
                        <TableCell>Content Deleted Date</TableCell>
                        <TableCell>ePublisher Status</TableCell>
                        <TableCell>Cancellation Fee</TableCell>
                        <TableCell>Cancelled By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cancelledSchools.map((cs) => {
                        const today = new Date();
                        const agreementEnd = new Date(cs.agreementEndDate);
                        const accountClosure = new Date(cs.accountClosureDate);
                        const contentDeleted = new Date(cs.contentDeletedDate);

                        // Derive expected ePublisher status from dates
                        let expectedStatus = 'open';
                        if (today >= contentDeleted) expectedStatus = 'deleted';
                        else if (today >= accountClosure) expectedStatus = 'closed';

                        const statusColor = {
                          open: 'success',
                          closed: 'warning',
                          deleted: 'error'
                        }[cs.ePublisherStatus] || 'default';

                        const statusActions = {
                          open: [{ label: 'Close Account', value: 'closed', color: 'warning' }],
                          closed: [{ label: 'Mark Deleted', value: 'deleted', color: 'error' }],
                          deleted: []
                        }[cs.ePublisherStatus] || [];

                        return (
                          <TableRow key={cs.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {cs.schoolName || `Order #${cs.schoolOrderId}`}
                              </Typography>
                              {cs.isEarlyTermination ? (
                                <Chip label="Early Termination" color="error" size="small" sx={{ mt: 0.3, height: 18, fontSize: '0.65rem' }} />
                              ) : (
                                <Chip label="Non-Renewal" color="default" size="small" sx={{ mt: 0.3, height: 18, fontSize: '0.65rem' }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {cs.cancellationReason}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(cs.agreementEndDate)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">{formatDate(cs.accountClosureDate)}</Typography>
                                {today >= accountClosure && cs.ePublisherStatus === 'open' && (
                                  <Chip label="Due" color="warning" size="small" sx={{ mt: 0.3, height: 16, fontSize: '0.6rem' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">{formatDate(cs.contentDeletedDate)}</Typography>
                                {today >= contentDeleted && cs.ePublisherStatus !== 'deleted' && (
                                  <Chip label="Due" color="error" size="small" sx={{ mt: 0.3, height: 16, fontSize: '0.6rem' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Chip
                                  label={cs.ePublisherStatus.charAt(0).toUpperCase() + cs.ePublisherStatus.slice(1)}
                                  color={statusColor}
                                  size="small"
                                  sx={{ mb: 0.5 }}
                                />
                                {statusActions.map(action => (
                                  <Box key={action.value}>
                                    <Button
                                      size="small"
                                      color={action.color}
                                      variant="outlined"
                                      onClick={() => handleUpdateEPublisherStatus(cs.id, action.value)}
                                      sx={{ fontSize: '0.65rem', px: 0.8, py: 0.2, textTransform: 'none', mt: 0.3 }}
                                    >
                                      {action.label}
                                    </Button>
                                  </Box>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {cs.totalCancellationFee != null ? (
                                <Box>
                                  <Typography variant="body2" fontWeight={700} color="error.main">
                                    ${parseFloat(cs.totalCancellationFee).toFixed(2)}
                                  </Typography>
                                  {cs.proRataDistCost != null && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Pro-rata: ${parseFloat(cs.proRataDistCost).toFixed(2)}
                                    </Typography>
                                  )}
                                  {cs.remainingMonths != null && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {parseFloat(cs.remainingMonths).toFixed(1)} months remaining
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{cs.cancelledByName || '�'}</Typography>
                              {cs.createdAt && (
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(cs.createdAt)}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>

          {/* New Schools Tab */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6">New Schools</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Schools that have ordered � automatically added when a School Enquiry status is set to Ordered.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchNewSchools}
                  disabled={newSchoolsLoading}
                  size="small"
                >
                  Refresh
                </Button>
              </Box>

              {newSchoolsError && <Alert severity="error" sx={{ mb: 2 }}>{newSchoolsError}</Alert>}

              {newSchoolsLoading ? (
                <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
              ) : newSchoolsData.length === 0 ? (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>No New Schools</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Schools will appear here when a School Enquiry is marked as Ordered.
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Authorised Date</TableCell>
                        <TableCell>Demo Date</TableCell>
                        <TableCell>Training Date</TableCell>
                        <TableCell>Account Created</TableCell>
                        <TableCell>1st Deployment</TableCell>
                        <TableCell>2nd Deployment</TableCell>
                        <TableCell>3rd Deployment</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {newSchoolsData.map((ns) => {
                        const statusColors = {
                          'Ordered': 'primary',
                          'Authorised': 'info',
                          'Waiting for Training': 'warning',
                          'Waiting for 1st Deployment': 'secondary',
                          '1st Deployment Done': 'success',
                          '2nd Deployment Done': 'success',
                          '3rd Deployment Done': 'success',
                          'Self Deploying': 'default'
                        };

                        // Calculate expected next deployment date using the 10-week term model
                        const calcExpected = (baseDate) => {
                          if (!baseDate || !ns.frequency) return null;
                          const base = new Date(baseDate);
                          if (isNaN(base.getTime())) return null;
                          const days = getFrequencyIntervalDays(ns.frequency);
                          if (!days) return null;
                          const d = new Date(base);
                          d.setDate(d.getDate() + days);
                          return d;
                        };

                        const exp2 = calcExpected(ns.deployment_1_date);
                        const exp3 = calcExpected(ns.deployment_2_date);

                        const DoneBtn = ({ field, label }) => (
                          <Tooltip title={`Mark ${label} as done today`}>
                            <Button size="small" variant="contained" color="success"
                              sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, textTransform: 'none', whiteSpace: 'nowrap' }}
                              onClick={() => handleQuickSetDate(ns.id, field, ns)}>
                              Mark Done
                            </Button>
                          </Tooltip>
                        );

                        const SetDateBtn = ({ field, label }) => (
                          <Tooltip title={`Set ${label} to today`}>
                            <Button size="small" variant="outlined" startIcon={<CalendarTodayIcon />}
                              sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, textTransform: 'none' }}
                              onClick={() => handleQuickSetDate(ns.id, field)}>
                              Set Date
                            </Button>
                          </Tooltip>
                        );

                        const PickDateBtn = ({ recordId, existingDate, label }) => (
                          <Tooltip title={`Pick a date for ${label}`}>
                            <Button size="small" variant="outlined" startIcon={<CalendarTodayIcon />}
                              sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, textTransform: 'none' }}
                              onClick={() => handleOpenTrainingDate(recordId, existingDate)}>
                              Set Date
                            </Button>
                          </Tooltip>
                        );

                        return (
                          <TableRow key={ns.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{ns.school_name}</Typography>
                              {ns.notes && (
                                <Tooltip title={ns.notes}>
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160, display: 'block' }}>
                                    {ns.notes}
                                  </Typography>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip label={ns.status} color={statusColors[ns.status] || 'default'} size="small" />
                            </TableCell>
                            <TableCell>
                              {ns.authorised_date ? formatDate(ns.authorised_date) : <Typography variant="caption" color="text.disabled">Not set</Typography>}
                            </TableCell>
                            <TableCell>
                              {ns.demo_date ? formatDate(ns.demo_date) : <Typography variant="caption" color="text.disabled">Not set</Typography>}
                            </TableCell>
                            <TableCell>
                              {ns.training_date ? (
                                <Box>
                                  <Typography variant="body2">{formatDate(ns.training_date)}</Typography>
                                  <Tooltip title="Change training date">
                                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                      onClick={() => handleOpenTrainingDate(ns.id, ns.training_date)}>
                                      Change
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <PickDateBtn recordId={ns.id} existingDate={ns.training_date} label="Training Date" />
                              )}
                            </TableCell>
                            <TableCell>
                              {ns.account_created_date ? formatDate(ns.account_created_date) : <SetDateBtn field="account_created_date" label="Account Created" />}
                            </TableCell>

                            {/* 1st Deployment */}
                            <TableCell>
                              {ns.deployment_1_date ? (
                                <Chip label={formatDate(ns.deployment_1_date)} size="small" color="success" variant="outlined" />
                              ) : (
                                <DoneBtn field="deployment_1_date" label="1st Deployment" />
                              )}
                            </TableCell>

                            {/* 2nd Deployment � actual date if set, otherwise expected + Mark Done */}
                            <TableCell>
                              {ns.deployment_2_date ? (
                                <Chip label={formatDate(ns.deployment_2_date)} size="small" color="success" variant="outlined" />
                              ) : ns.deployment_1_date ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                  {exp2 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Expected: {formatDate(exp2)}
                                    </Typography>
                                  )}
                                  <DoneBtn field="deployment_2_date" label="2nd Deployment" />
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.disabled">After 1st</Typography>
                              )}
                            </TableCell>

                            {/* 3rd Deployment � actual date if set, otherwise expected + Mark Done */}
                            <TableCell>
                              {ns.deployment_3_date ? (
                                <Chip label={formatDate(ns.deployment_3_date)} size="small" color="success" variant="outlined" />
                              ) : ns.deployment_2_date ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                  {exp3 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Expected: {formatDate(exp3)}
                                    </Typography>
                                  )}
                                  <DoneBtn field="deployment_3_date" label="3rd Deployment" />
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.disabled">After 2nd</Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              {ns.frequency
                                ? <Chip label={formatFrequency(ns.frequency)} size="small" variant="outlined" />
                                : <Typography variant="caption" color="text.disabled">�</Typography>}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                {ns.status === '3rd Deployment Done' && !ns.self_deploying_date && (
                                  <Tooltip title="Mark school as self-deploying from today">
                                    <Button size="small" variant="contained" color="secondary"
                                      sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, textTransform: 'none', whiteSpace: 'nowrap' }}
                                      onClick={() => handleQuickSetDate(ns.id, 'self_deploying_date')}>
                                      Self Deploying
                                    </Button>
                                  </Tooltip>
                                )}
                                {ns.self_deploying_date && (
                                  <Chip label={`Self Deploying: ${formatDate(ns.self_deploying_date)}`} size="small" color="default" variant="outlined" />
                                )}
                                <Tooltip title="Edit record">
                                  <IconButton size="small" onClick={() => handleOpenNewSchoolEdit(ns)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>
        </Paper>

        {/* New School Edit Dialog */}
        <Dialog open={newSchoolEditDialog.open} onClose={() => setNewSchoolEditDialog({ open: false, record: null })} maxWidth="md" fullWidth>
          <DialogTitle>Edit New School � {newSchoolEditDialog.record?.school_name}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth label="School Name" value={newSchoolForm.school_name}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, school_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={newSchoolForm.status}
                    onChange={(e) => setNewSchoolForm({ ...newSchoolForm, status: e.target.value })}>
                    <MenuItem value="Ordered">Ordered</MenuItem>
                    <MenuItem value="Authorised">Authorised</MenuItem>
                    <MenuItem value="Waiting for Training">Waiting for Training</MenuItem>
                    <MenuItem value="Waiting for 1st Deployment">Waiting for 1st Deployment</MenuItem>
                    <MenuItem value="1st Deployment Done">1st Deployment Done</MenuItem>
                    <MenuItem value="2nd Deployment Done">2nd Deployment Done</MenuItem>
                    <MenuItem value="3rd Deployment Done">3rd Deployment Done</MenuItem>
                    <MenuItem value="Self Deploying">Self Deploying</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Key Dates</Typography></Divider></Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="Authorised Date" type="date" InputLabelProps={{ shrink: true }}
                  value={newSchoolForm.authorised_date}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, authorised_date: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="Demo Date" type="date" InputLabelProps={{ shrink: true }}
                  value={newSchoolForm.demo_date}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, demo_date: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="Training Date" type="date" InputLabelProps={{ shrink: true }}
                  value={newSchoolForm.training_date}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, training_date: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="Account Created Date" type="date" InputLabelProps={{ shrink: true }}
                  value={newSchoolForm.account_created_date}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, account_created_date: e.target.value })} />
              </Grid>

              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Deployment Dates</Typography></Divider></Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="1st Deployment Date" type="date" InputLabelProps={{ shrink: true }}
                  value={newSchoolForm.deployment_1_date}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, deployment_1_date: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="2nd Deployment Date" type="date" InputLabelProps={{ shrink: true }}
                  value={newSchoolForm.deployment_2_date}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, deployment_2_date: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="3rd Deployment Date" type="date" InputLabelProps={{ shrink: true }}
                  value={newSchoolForm.deployment_3_date}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, deployment_3_date: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select label="Frequency" value={newSchoolForm.frequency}
                    onChange={(e) => setNewSchoolForm({ ...newSchoolForm, frequency: e.target.value })}>
                    <MenuItem value="">Not set</MenuItem>
                    <MenuItem value="Weekly">Weekly (~7 days)</MenuItem>
                    <MenuItem value="Fortnightly">Fortnightly (~14 days)</MenuItem>
                    <MenuItem value="Monthly">Monthly (~30 days)</MenuItem>
                    <MenuItem value="1 per term">1 per term (~10 weeks)</MenuItem>
                    <MenuItem value="2 per term">2 per term (~5 weeks)</MenuItem>
                    <MenuItem value="3 per term">3 per term (~3.5 weeks)</MenuItem>
                    <MenuItem value="4 per term">4 per term (~2.5 weeks)</MenuItem>
                    <MenuItem value="Term">Once per term</MenuItem>
                    <MenuItem value="Yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notes" multiline rows={3} value={newSchoolForm.notes}
                  onChange={(e) => setNewSchoolForm({ ...newSchoolForm, notes: e.target.value })} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewSchoolEditDialog({ open: false, record: null })}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveNewSchool}>Save Changes</Button>
          </DialogActions>
        </Dialog>

        {/* Training Date Picker Dialog */}
        <Dialog open={trainingDateDialog.open} onClose={() => setTrainingDateDialog({ open: false, recordId: null, date: '' })} maxWidth="xs" fullWidth>
          <DialogTitle>Set Training Date</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth label="Training Date" type="date"
              InputLabelProps={{ shrink: true }}
              value={trainingDateDialog.date}
              onChange={(e) => setTrainingDateDialog({ ...trainingDateDialog, date: e.target.value })}
              sx={{ mt: 1 }}
              helperText="If the training date is today or in the past, status will advance to Waiting for 1st Deployment automatically."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTrainingDateDialog({ open: false, recordId: null, date: '' })}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveTrainingDate} disabled={!trainingDateDialog.date}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Order Detail Dialog */}
        <OrderDetail
          open={openDetailDialog}
          onClose={handleCloseDetailDialog}
          order={selectedOrder}
          onUpdate={handleOrderUpdate}
        />

        {/* Notes Dialog */}
        <OrderWorkflowNotesDialog
          open={notesDialog.open}
          onClose={handleCloseNotesDialog}
          title={notesDialog.title}
          entityType={notesDialog.entityType}
          entityId={notesDialog.entityId}
          initialNotes={notesDialog.notes}
          onNotesUpdate={handleNotesUpdate}
        />

        {/* School Invoice Dialog */}
        <SchoolInvoiceDialog
          open={invoiceDialogOpen}
          onClose={handleCloseInvoiceDialog}
          order={orderToInvoice}
          onInvoiceGenerated={handleInvoiceGenerated}
        />

        {/* Create Organisation Dialog */}
        <Dialog
          open={createOrgDialog.open}
          onClose={() => !createOrgDialog.loading && setCreateOrgDialog({ open: false, order: null, loading: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Organisation</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              This will create a new organization from the school order details submitted via the signup form
              and link it to this order.
            </Typography>
            {createOrgDialog.order && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2"><strong>School:</strong> {createOrgDialog.order.schoolName || '�'}</Typography>
                <Typography variant="body2"><strong>Contact:</strong> {createOrgDialog.order.contactPerson || '�'}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {createOrgDialog.order.email || '�'}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {createOrgDialog.order.phone || '�'}</Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              You can edit the organization's full details after it's created.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setCreateOrgDialog({ open: false, order: null, loading: false })}
              disabled={createOrgDialog.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateOrganization}
              disabled={createOrgDialog.loading}
            >
              {createOrgDialog.loading ? 'Creating�' : 'Create Organisation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert 
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* New School Order Dialog */}
        <Dialog
          open={newSchoolDialogOpen}
          onClose={() => !newSchoolFormLoading && setNewSchoolDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>New School Order</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
              Enter the school name to create a signup order and generate a unique enrolment link to send to the school.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="School Name"
              placeholder="e.g. Springfield Primary School"
              value={newSchoolName}
              onChange={(e) => setNewSchoolName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !newSchoolFormLoading && newSchoolName.trim() && handleGenerateNewSchoolForm()}
              disabled={newSchoolFormLoading || !!newSchoolFormUrl}
              required
            />
            {newSchoolFormUrl && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600} color="success.dark" gutterBottom>
                  Form link generated and copied to clipboard!
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', display: 'block', mb: 1.5 }}>
                  {newSchoolFormUrl}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={() => {
                      navigator.clipboard.writeText(newSchoolFormUrl);
                      setSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Copy Link
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(newSchoolFormUrl, '_blank')}
                    sx={{ textTransform: 'none' }}
                  >
                    Preview Form
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setNewSchoolDialogOpen(false)}
              disabled={newSchoolFormLoading}
            >
              {newSchoolFormUrl ? 'Close' : 'Cancel'}
            </Button>
            {!newSchoolFormUrl && (
              <Button
                variant="contained"
                onClick={handleGenerateNewSchoolForm}
                disabled={!newSchoolName.trim() || newSchoolFormLoading}
              >
                {newSchoolFormLoading ? 'Generating...' : 'Generate Link'}
              </Button>
            )}
            {newSchoolFormUrl && (
              <Button
                variant="outlined"
                onClick={() => {
                  setNewSchoolFormUrl(null);
                  setNewSchoolName('');
                }}
              >
                New School
              </Button>
            )}
          </DialogActions>
        </Dialog>

      </Container>

      {/* ── Cancel Choice Dialog (School Orders tab) ─────────────────────── */}
      <Dialog
        open={cancelChoiceDialog.open}
        onClose={() => setCancelChoiceDialog({ open: false, order: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel School Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            How would you like to cancel the order for{' '}
            <strong>{cancelChoiceDialog.order?.schoolName || 'this school'}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2, pt: 0, alignItems: 'stretch' }}>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleSimpleOrderCancel}
            startIcon={<CancelIcon />}
          >
            Cancel Order Only
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => openEarlyCancelDialog(cancelChoiceDialog.order)}
            startIcon={<CancelledIcon />}
          >
            Cancel School Early (with fee + Xero invoice)
          </Button>
          <Button onClick={() => setCancelChoiceDialog({ open: false, order: null })}>
            Keep Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Early Cancel Dialog (School Orders tab) ───────────────────────── */}
      {(() => {
        const feePreview = computeEarlyCancelFeePreview();
        return (
          <Dialog
            open={earlyCancelDialog.open}
            onClose={() => { if (!earlyCancelDialog.loading) setEarlyCancelDialog(prev => ({ ...prev, open: false })); }}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={earlyCancelDialog.loading}
          >
            <DialogTitle sx={{ pb: 1 }}>
              {earlyCancelDialog.result ? 'School Cancelled Early' : 'Cancel School Early'}
            </DialogTitle>

            {earlyCancelDialog.result ? (
              <>
                <DialogContent>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {earlyCancelDialog.order?.schoolName || 'The school'} has been cancelled and added to the Cancelled Schools list.
                    Any active ad order salespeople have been notified.
                  </Alert>
                  {earlyCancelDialog.warning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>{earlyCancelDialog.warning}</Alert>
                  )}
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Agreement End Date</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatDate(earlyCancelDialog.result.agreementEndDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Account Closure Date (1 month later)</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatDate(earlyCancelDialog.result.accountClosureDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Content Deleted Date (3 months after closure)</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatDate(earlyCancelDialog.result.contentDeletedDate)}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ p: 1.5, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                      <Typography variant="caption" color="error.main">Early Termination Fee</Typography>
                      <Typography variant="h6" color="error.main" fontWeight={700}>
                        ${parseFloat(earlyCancelDialog.result.totalCancellationFee).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ${SCHOOL_EARLY_CANCEL_FEE.toFixed(2)} early termination fee + pro-rata distribution costs
                        ({earlyCancelDialog.result.remainingMonths} months remaining × ${parseFloat(earlyCancelDialog.result.paidSchoolPrice / 12).toFixed(2)}/month)
                      </Typography>
                    </Box>
                    {earlyCancelDialog.result.xeroInvoiceUrl && (
                      <Button
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        href={earlyCancelDialog.result.xeroInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        fullWidth
                      >
                        View Xero Invoice
                      </Button>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button variant="contained" onClick={() => setEarlyCancelDialog(prev => ({ ...prev, open: false }))}>Close</Button>
                </DialogActions>
              </>
            ) : (
              <>
                <DialogContent>
                  <DialogContentText sx={{ mb: 2 }}>
                    You are early-cancelling the school order for{' '}
                    <strong>{earlyCancelDialog.order?.schoolName || 'this school'}</strong>.
                    An early termination fee will be calculated and a Xero invoice generated automatically.
                  </DialogContentText>

                  {earlyCancelDialog.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{earlyCancelDialog.error}</Alert>
                  )}

                  <Stack spacing={2.5}>
                    <TextField
                      label="Agreement End Date"
                      type="date"
                      value={earlyCancelDialog.agreementEndDate}
                      onChange={e => setEarlyCancelDialog(prev => ({ ...prev, agreementEndDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      disabled={earlyCancelDialog.loading}
                      helperText="Pre-filled from the order's end date. Adjust if needed."
                    />
                    <TextField
                      label="Cancellation Reason"
                      multiline
                      minRows={3}
                      value={earlyCancelDialog.cancellationReason}
                      onChange={e => setEarlyCancelDialog(prev => ({ ...prev, cancellationReason: e.target.value }))}
                      fullWidth
                      disabled={earlyCancelDialog.loading}
                      placeholder="Reason for early cancellation…"
                    />

                    {feePreview && (
                      <Box sx={{ p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                        <Typography variant="subtitle2" color="warning.dark" gutterBottom>Estimated Cancellation Fee</Typography>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Early termination fee</Typography>
                            <Typography variant="body2" fontWeight={600}>${SCHOOL_EARLY_CANCEL_FEE.toFixed(2)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Pro-rata ({feePreview.remainingMonths} months × ${(earlyCancelDialog.paidSchoolPrice / 12).toFixed(2)})
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>${feePreview.proRata.toFixed(2)}</Typography>
                          </Box>
                          <Divider sx={{ my: 0.5 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" fontWeight={700}>Total (inc. GST)</Typography>
                            <Typography variant="body2" fontWeight={700} color="error.main">${feePreview.total.toFixed(2)}</Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => setEarlyCancelDialog(prev => ({ ...prev, open: false }))}
                    disabled={earlyCancelDialog.loading}
                  >
                    Keep Order
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirmEarlyCancelSchool}
                    disabled={earlyCancelDialog.loading}
                    startIcon={earlyCancelDialog.loading ? <CircularProgress size={16} /> : <CancelledIcon />}
                  >
                    {earlyCancelDialog.loading ? 'Cancelling…' : 'Confirm Early Cancellation'}
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        );
      })()}

      {/* ── Cancel School Dialog (Reorders tab) ────────────────────────────── */}
      {(() => {
        const feePreview = computeCancelFeePreview();
        return (
          <Dialog
            open={cancelDialog.open}
            onClose={closeCancelDialog}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={cancelDialog.loading}
          >
            <DialogTitle sx={{ pb: 1 }}>
              {cancelDialog.result ? 'School Order Cancelled' : 'Cancel School Order'}
            </DialogTitle>

            {cancelDialog.result ? (
              <>
                <DialogContent>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    The school has been cancelled and added to the Cancelled Schools list.
                    {cancelDialog.reorder?.id ? ' Any active ad order salespeople have been notified.' : ''}
                  </Alert>
                  <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Agreement End Date</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatDate(cancelDialog.result.agreementEndDate)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Account Closure Date (1 month later)</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatDate(cancelDialog.result.accountClosureDate)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Content Deleted Date (3 months after closure)</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatDate(cancelDialog.result.contentDeletedDate)}</Typography>
                      </Box>
                      {cancelDialog.result.totalCancellationFee != null && (
                        <>
                          <Divider />
                          <Box sx={{ p: 1.5, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                            <Typography variant="caption" color="error.main">Early Termination Fee</Typography>
                            <Typography variant="h6" color="error.main" fontWeight={700}>
                              ${parseFloat(cancelDialog.result.totalCancellationFee).toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              An early termination fee of $1,450.00 plus the pro rata distribution costs of the service
                              for the remaining duration of the 12 month service is applicable.
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                  <Button variant="contained" onClick={closeCancelDialog}>Close</Button>
                </DialogActions>
              </>
            ) : (
              <>
                <DialogContent>
                  <DialogContentText sx={{ mb: 2 }}>
                    Are you sure you want to cancel the school order for{' '}
                    <strong>{cancelDialog.reorder?.schoolName || 'this school'}</strong>?
                    This will notify any salespeople with active ad orders linked to this school.
                  </DialogContentText>

                  {!cancelDialog.reorder?.id && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This school has no active order on file. Confirming will mark their school status as Cancelled and add them to the Cancelled Schools tab to follow the cancellation process.
                    </Alert>
                  )}

                  {cancelDialog.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{cancelDialog.error}</Alert>
                  )}

                  <Stack spacing={2.5}>
                    <TextField
                      label="Agreement End Date"
                      type="date"
                      value={cancelDialog.agreementEndDate}
                      onChange={(e) => setCancelDialog(prev => ({ ...prev, agreementEndDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      helperText="The date the school cancels or agreement ends"
                      fullWidth
                      size="small"
                      required
                    />

                    <TextField
                      label="Reason for cancellation / non-renewal"
                      value={cancelDialog.cancellationReason}
                      onChange={(e) => setCancelDialog(prev => ({ ...prev, cancellationReason: e.target.value }))}
                      multiline
                      minRows={3}
                      fullWidth
                      size="small"
                      required
                      placeholder="e.g. School did not renew - budget constraints"
                    />

                    <Divider />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={cancelDialog.isEarlyTermination}
                          onChange={(e) => setCancelDialog(prev => ({ ...prev, isEarlyTermination: e.target.checked, paidModelCost: '' }))}
                          color="error"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Early termination (mid-order cancellation - cancellation fee applies)
                        </Typography>
                      }
                    />

                    {cancelDialog.isEarlyTermination && (
                      <Box sx={{ pl: 1 }}>
                        <TextField
                          label="Annual cost under the Paid model ($)"
                          value={cancelDialog.paidModelCost}
                          onChange={(e) => setCancelDialog(prev => ({ ...prev, paidModelCost: e.target.value }))}
                          type="number"
                          inputProps={{ min: 0, step: '0.01' }}
                          size="small"
                          fullWidth
                          required
                          helperText={
                            cancelDialog.reorder?.paymentModel === 'Ad Supported'
                              ? 'School is on Ad Supported - enter the equivalent Paid model annual cost'
                              : 'Enter the annual cost under the Paid model'
                          }
                        />

                        {feePreview && (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                            <Typography variant="caption" color="warning.dark" display="block" gutterBottom>
                              Estimated Cancellation Fee
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Annual paid cost: <strong>${parseFloat(cancelDialog.paidModelCost).toFixed(2)}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Remaining: <strong>{feePreview.remainingMonths} months</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pro-rata distribution cost: <strong>${feePreview.proRata.toFixed(2)}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Early termination fee: <strong>$1,450.00</strong>
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body1" fontWeight={700} color="error.main">
                              Total: ${feePreview.total.toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              An early termination fee of $1,450.00 plus the pro rata distribution costs of
                              the service for the remaining duration of the 12 month service is applicable.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button onClick={closeCancelDialog} disabled={cancelDialog.loading}>
                    Keep Order
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirmCancelSchool}
                    disabled={cancelDialog.loading}
                    startIcon={cancelDialog.loading ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
                  >
                    {cancelDialog.loading ? 'Cancelling...' : 'Confirm Cancellation'}
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        );
      })()}

    </>
  );
};

export default SchoolWorkflow;